import { useState, useEffect, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "react-query";
import { Auth } from "shared/@auth/Auth";
import { useLocation } from "react-router-dom";
import { createContainer } from "unstated-next";

// Hook for GET operations
export function useDashboardQueries(
  manualRefetchInterval,
  manualRefetch,
  active = false
) {
  const { makeAuthedRequest } = Auth.useContainer();
  const [status, setStatus] = useState(active ? "loading" : "idle");

  const location = useLocation();
  function pathParam(location) {
    return location.includes("executive-committee") ? "EC" : "MT"; // possible change back to EC if there is an ec specific API
  }
  const [_location, setLocation] = useState(null);

  const [refetchInterval, setRefetchInterval] = useState(false);
  useMemo(() => {
    if (manualRefetch !== undefined && manualRefetchInterval !== undefined) {
      setRefetchInterval(manualRefetch ? manualRefetchInterval : 10 * 1000);
    } else {
      setRefetchInterval(false);
    }
  }, [manualRefetch, manualRefetchInterval]);

  const getRefetchInterval = () => {
    return refetchInterval;
  };

  useMemo(() => {
    setLocation(pathParam(location.pathname));
  }, [location]);

  // Dashboard Data
  const {
    data: dashboardData,
    refetch,
    isFetching,
  } = useQuery(
    ["dashboard", location.pathname],
    () =>
      makeAuthedRequest(
        `/management-team/${pathParam(location.pathname)}/dashboard`,
        {
          method: "GET",
        }
      ).then(async (res) => {
        if (res.ok) {
          setStatus("success");
          return res.json();
        } else {
          setStatus("error");
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      staleTime: 0,
      //  enabled: active, // prevent automatic firing when inactive
      refetchOnMount: true,
    }
  );

  // Destructure data object from API. Return empty defaults in case data is null.
  const { dashboard: data } = useMemo(() => {
    if (dashboardData == null) {
      return {
        dashboard: [],
      };
    } else {
      return {
        dashboard: dashboardData,
      };
    }
  }, [dashboardData]);

  // fetch agenda file
  const fetchImage = (url, fileName) => {
    fetch(url)
      .then((response) => response.text())
      .then((res) => {
        // create local url for image and download
        const link = document.createElement("a");
        link.href = res;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      });
  };

  const fetchFile = (fileName) =>
    makeAuthedRequest(`/management-team/downloadfile/agendas/${fileName}`, {
      method: "GET",
    })
      .then((response) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          fetchImage(url, fileName);
        });
      })
      .catch((err) => {
        console.log(err);
      });

  const getAvailableMeetings = async () =>
    makeAuthedRequest(`/management-team/meetings/available`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        return await res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    });

  return {
    data,
    status,
    fetchFile,
    getAvailableMeetings,
    refetchDashboard: refetch,
    isFetching,
  };
}

// Hook for mutations (PUT, POST operations)
export function useDashboardMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  // meeting end put
  const meetingEndPut = useMutation(["meeting"], async (meetingEndObject) =>
    makeAuthedRequest(`/management-team/meeting/end`, {
      method: "PUT",
      body: JSON.stringify(meetingEndObject),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const meetingEndSessionPut = useMutation(
    ["meeting"],
    async (meetingEndObject) =>
      makeAuthedRequest(`/management-team/meeting/end-session`, {
        method: "PUT",
        body: JSON.stringify(meetingEndObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // meeting check in put
  const meetingCheckInPut = useMutation(
    ["meeting"],
    async (meetingCheckInObject) =>
      makeAuthedRequest(`/management-team/meeting/checkin`, {
        method: "PUT",
        body: JSON.stringify(meetingCheckInObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // user edit put
  const userEditPut = useMutation(
    async (userEditObject) =>
      await makeAuthedRequest(`/management-team/user/edit`, {
        method: "PUT",
        body: JSON.stringify(userEditObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // user settings put
  const userSettingsPut = useMutation(
    async ({ year, userEditObject }) =>
      makeAuthedRequest(`/management-team/settings/edit/${year}`, {
        method: "PUT",
        body: JSON.stringify(userEditObject),
      }).then(async (res) => {
        if (res.ok) {
          return;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  // Edit Agenda
  const editAgendaPut = useMutation(["editAgenda"], (agendaObject) =>
    makeAuthedRequest(`/management-team/agenda/edit`, {
      method: "PUT",
      body: JSON.stringify(agendaObject),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const swapIBOPost = useMutation((userId) =>
    makeAuthedRequest(`/management-team/diamond-swap/${userId}`, {
      method: "POST",
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  return {
    meetingEndPut,
    meetingEndSessionPut,
    meetingCheckInPut,
    userEditPut,
    userSettingsPut,
    editAgendaPut,
    swapIBOPost,
  };
}

// Combined hook for backward compatibility
export function useDashboard(
  manualRefetchInterval,
  manualRefetch,
  active = false
) {
  const {
    data,
    status,
    fetchFile,
    getAvailableMeetings,
    refetchDashboard,
    isFetching,
  } = useDashboardQueries(manualRefetchInterval, manualRefetch, active);
  const {
    meetingEndPut,
    meetingEndSessionPut,
    meetingCheckInPut,
    userEditPut,
    userSettingsPut,
    editAgendaPut,
    swapIBOPost,
  } = useDashboardMutations();

  return {
    data,
    status,
    fetchFile,
    meetingEndPut,
    meetingEndSessionPut,
    meetingCheckInPut,
    userEditPut,
    userSettingsPut,
    editAgendaPut,
    swapIBOPost,
    getAvailableMeetings,
    refetchDashboard,
    isFetching,
  };
}

/**
 * Exported as an unstated container for sharing auth state between components.
 * Provider must be mounted higher in the component tree in order to use the container
 * from inside a component.
 */
export const Dashboard = createContainer(useDashboard);
